import type {
  DailyWeather,
  GetCurrentWeatherResult,
  HourlyWeather,
  WeatherAttribution,
} from '@/shared/domain'

type RawHourly = {
  time: string
  temperatureF: number
  condition: string
  windSpeedMph: number
  windGustMph: number
  windDegree: number
  precipitationProbability: number
  pressureInHg: number
}

type RawDay = {
  condition: string
  currentTemperatureF: number
  minTemperatureF: number
  maxTemperatureF: number
  feelsLikeF: number
  windSpeedMph: number
  windGustMph: number
  windDegree: number
  precipitationProbability: number
  sunrise: string
  sunset: string
  tzoffset: number
  hourlyBreakdown?: RawHourly[]
  date: string
  moonPhase?: number
  moonPhaseLabel?: string
  moonIlluminationPercent?: number
}

function parseLocalDate(dateInput: string): Date {
  const [year, month, day] = dateInput.slice(0, 10).split('-').map(Number)
  return new Date(year, month - 1, day)
}

function mapHourly(raw: RawHourly): HourlyWeather {
  return {
    time: new Date(raw.time),
    temperatureF: raw.temperatureF,
    condition: raw.condition,
    windSpeedMph: raw.windSpeedMph,
    windGustMph: raw.windGustMph,
    windDegree: raw.windDegree,
    precipitationProbability: raw.precipitationProbability,
    pressureInHg: raw.pressureInHg,
  }
}

function mapDay(raw: RawDay): DailyWeather {
  return {
    condition: raw.condition,
    currentTemperatureF: raw.currentTemperatureF,
    minTemperatureF: raw.minTemperatureF,
    maxTemperatureF: raw.maxTemperatureF,
    feelsLikeF: raw.feelsLikeF,
    windSpeedMph: raw.windSpeedMph,
    windGustMph: raw.windGustMph,
    windDegree: raw.windDegree,
    precipitationProbability: raw.precipitationProbability,
    sunrise: new Date(raw.sunrise),
    sunset: new Date(raw.sunset),
    tzoffset: raw.tzoffset,
    hourlyBreakdown: (raw.hourlyBreakdown ?? []).map(mapHourly),
    date: parseLocalDate(raw.date),
    ...(raw.moonPhase !== undefined && { moonPhase: raw.moonPhase }),
    ...(raw.moonPhaseLabel !== undefined && { moonPhaseLabel: raw.moonPhaseLabel }),
    ...(raw.moonIlluminationPercent !== undefined && {
      moonIlluminationPercent: raw.moonIlluminationPercent,
    }),
  }
}

type RawAttribution = {
  name: string
  url: string
}

export function mapWeatherResponse(data: {
  current: RawDay
  forecast: RawDay[]
  attribution?: RawAttribution
}): GetCurrentWeatherResult {
  const attribution: WeatherAttribution | undefined = data.attribution
    ? { name: data.attribution.name, url: data.attribution.url }
    : undefined
  return {
    current: mapDay(data.current),
    forecast: data.forecast.map(mapDay),
    ...(attribution && { attribution }),
  }
}
